import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Toaster, toast } from "sonner";
import { Loader2, LogOut, Plus, Trash2, Download, Lock, Calendar as CalendarIcon, RefreshCw } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "bw_admin_token";
const nf = (n) => new Intl.NumberFormat("en-IE").format(Math.round(n || 0));

const inputCls =
  "w-full rounded-none border-0 border-b border-white/25 bg-transparent px-0 py-3 font-inter text-base text-white placeholder:text-white/40 focus:border-white focus:outline-none focus:ring-0 transition-colors";

const emptyForm = {
  donor_name: "",
  business_name: "",
  address: "",
  email: "",
  phone: "",
  plaque_message: "",
  amount_eur: 1000,
  tax_relief_consent: false,
  donated_at: "",
};

/* ---------- Login gate ---------- */
const LoginGate = ({ onAuthed }) => {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { token: pw });
      localStorage.setItem(TOKEN_KEY, pw);
      onAuthed(pw);
    } catch (_) {
      toast.error("Incorrect password.");
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-black px-6">
      <form onSubmit={submit} className="w-full max-w-sm border border-white/20 bg-[#050505] p-10" data-testid="admin-login-form">
        <Lock className="mb-6 h-6 w-6 text-white/70" />
        <p className="label-caps mb-2 text-white/60">Walterstown GFC</p>
        <h1 className="mb-8 font-oswald text-3xl font-bold uppercase text-white">Admin Access</h1>
        <label className="label-caps mb-3 block text-white/60">Admin Password</label>
        <input
          data-testid="admin-password-input"
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="Enter password"
          className={inputCls}
          autoFocus
        />
        <button
          data-testid="admin-login-btn"
          type="submit"
          disabled={loading || !pw}
          className="mt-8 flex w-full items-center justify-center gap-3 bg-white px-6 py-4 font-oswald text-sm uppercase tracking-[0.2em] text-black transition-colors hover:bg-white/80 disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Log In"}
        </button>
      </form>
    </div>
  );
};

/* ---------- Add donation form ---------- */
const AddDonation = ({ token, onAdded }) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.donor_name || !form.plaque_message) {
      toast.error("Name and plaque message are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount_eur: Number(form.amount_eur) || 0,
        donated_at: form.donated_at ? new Date(form.donated_at).toISOString() : null,
      };
      await axios.post(`${API}/admin/offline-donation`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Bank-transfer donation added.");
      setForm(emptyForm);
      onAdded();
    } catch (_) {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-white/20 bg-[#050505] p-6 md:p-8" data-testid="add-donation-form">
      <h2 className="mb-6 flex items-center gap-2 font-oswald text-xl font-bold uppercase text-white">
        <Plus className="h-5 w-5" /> Log Bank-Transfer Donation
      </h2>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <label className="label-caps mb-2 block text-white/60">Name *</label>
          <input data-testid="add-name" value={form.donor_name} onChange={set("donor_name")} className={inputCls} placeholder="e.g. John Murphy" />
        </div>
        <div>
          <label className="label-caps mb-2 block text-white/60">Business (optional)</label>
          <input data-testid="add-business" value={form.business_name} onChange={set("business_name")} className={inputCls} placeholder="Optional" />
        </div>
        <div className="md:col-span-2">
          <label className="label-caps mb-2 block text-white/60">Address</label>
          <input data-testid="add-address" value={form.address} onChange={set("address")} className={inputCls} placeholder="Street, town, county, Eircode" />
        </div>
        <div>
          <label className="label-caps mb-2 block text-white/60">Email</label>
          <input data-testid="add-email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" />
        </div>
        <div>
          <label className="label-caps mb-2 block text-white/60">Phone</label>
          <input data-testid="add-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="087 123 4567" />
        </div>
        <div className="md:col-span-2">
          <label className="label-caps mb-2 block text-white/60">Plaque Message / Name *</label>
          <input data-testid="add-plaque" value={form.plaque_message} onChange={set("plaque_message")} maxLength={200} className={inputCls} placeholder="Exactly as it should be engraved" />
        </div>
        <div>
          <label className="label-caps mb-2 block text-white/60">Amount (€)</label>
          <input data-testid="add-amount" type="number" value={form.amount_eur} onChange={set("amount_eur")} className={inputCls} />
        </div>
        <div>
          <label className="label-caps mb-2 block text-white/60">Date Received (optional)</label>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="add-date"
                className={`${inputCls} flex items-center justify-between text-left ${form.donated_at ? "text-white" : "text-white/40"}`}
              >
                {form.donated_at ? format(form.donated_at, "dd/MM/yyyy") : "Select date"}
                <CalendarIcon className="h-4 w-4 text-white/50" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto border-white/20 bg-[#0a0a0a] p-0 text-white" align="start">
              <Calendar
                mode="single"
                selected={form.donated_at || undefined}
                onSelect={(d) => setForm((f) => ({ ...f, donated_at: d || "" }))}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
      <label className="mt-6 flex cursor-pointer items-center gap-3" data-testid="add-tax-consent">
        <input
          type="checkbox"
          checked={form.tax_relief_consent}
          onChange={(e) => setForm((f) => ({ ...f, tax_relief_consent: e.target.checked }))}
          className="h-4 w-4 accent-white"
        />
        <span className="font-inter text-sm text-white/75">Donor consented to tax relief</span>
      </label>
      <button
        data-testid="add-submit-btn"
        type="submit"
        disabled={saving}
        className="mt-8 flex items-center gap-3 bg-white px-8 py-4 font-oswald text-sm uppercase tracking-[0.2em] text-black transition-colors hover:bg-white/80 disabled:opacity-50"
      >
        {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-4 w-4" />}
        Add Donation
      </button>
    </form>
  );
};

/* ---------- Site visits panel ---------- */
const SOURCE_LABELS = {
  direct: "Direct / Unknown",
  email: "Members Email",
  instagram: "Instagram",
  facebook: "Facebook",
  "members-email": "Members Email",
};
const labelFor = (s) => SOURCE_LABELS[s] || s.charAt(0).toUpperCase() + s.slice(1);

const VisitsPanel = ({ visits }) => {
  if (!visits) return null;
  const maxDay = Math.max(1, ...visits.daily.map((d) => d.count));
  const cards = [
    { label: "Total Visits", value: nf(visits.total_visits), testid: "visits-total" },
    { label: "Unique Visitors", value: nf(visits.unique_visitors), testid: "visits-unique" },
    { label: "Today", value: nf(visits.today), testid: "visits-today" },
    { label: "Last 7 Days", value: nf(visits.last_7_days), testid: "visits-7days" },
  ];
  return (
    <div className="mb-8 border border-white/20 bg-[#050505] p-6 md:p-8" data-testid="visits-panel">
      <h2 className="mb-6 font-oswald text-xl font-bold uppercase text-white">Site Visits</h2>
      <div className="mb-8 grid grid-cols-2 gap-px border border-white/15 bg-white/15 md:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-black p-5">
            <p className="label-caps mb-2 text-white/55">{c.label}</p>
            <p className="font-oswald text-3xl font-bold text-white" data-testid={c.testid}>{c.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* By source */}
        <div>
          <p className="label-caps mb-4 text-white/55">Where Visitors Came From</p>
          {visits.by_source.length === 0 ? (
            <p className="text-sm text-white/50">No visits recorded yet.</p>
          ) : (
            <div className="space-y-3" data-testid="visits-by-source">
              {visits.by_source.map((s) => {
                const pct = visits.total_visits ? Math.round((s.count / visits.total_visits) * 100) : 0;
                return (
                  <div key={s.source}>
                    <div className="mb-1 flex items-center justify-between text-sm">
                      <span className="text-white/80">{labelFor(s.source)}</span>
                      <span className="font-oswald font-bold text-white">{nf(s.count)} <span className="text-white/40">({pct}%)</span></span>
                    </div>
                    <div className="h-2 w-full bg-white/10">
                      <div className="h-full bg-[#43C46A]" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Daily trend */}
        <div>
          <p className="label-caps mb-4 text-white/55">Last 14 Days</p>
          <div className="flex h-32 items-end gap-1" data-testid="visits-daily">
            {visits.daily.map((d) => (
              <div key={d.day} className="group relative flex h-full flex-1 items-end" title={`${d.day}: ${d.count}`}>
                <div
                  className="w-full bg-white/70 transition-colors group-hover:bg-white"
                  style={{ height: `${Math.max(2, (d.count / maxDay) * 100)}%` }}
                />
              </div>
            ))}
          </div>
          <div className="mt-2 flex justify-between text-[10px] uppercase tracking-wider text-white/40">
            <span>{visits.daily[0]?.day.slice(5)}</span>
            <span>{visits.daily[visits.daily.length - 1]?.day.slice(5)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- Main dashboard ---------- */
export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [donations, setDonations] = useState([]);
  const [visits, setVisits] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (tok) => {
    setLoading(true);
    try {
      const hdr = { headers: { Authorization: `Bearer ${tok}` } };
      const [don, vis] = await Promise.all([
        axios.get(`${API}/admin/donations`, hdr),
        axios.get(`${API}/admin/visits`, hdr),
      ]);
      setDonations(don.data.donations);
      setVisits(vis.data);
      setAuthed(true);
    } catch (_) {
      localStorage.removeItem(TOKEN_KEY);
      setAuthed(false);
    } finally {
      setLoading(false);
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
    else setChecking(false);
  }, [token, load]);

  const onAuthed = (tok) => {
    setToken(tok);
    setAuthed(true);
    load(tok);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
  };

  const [reconciling, setReconciling] = useState(false);
  const reconcile = async () => {
    setReconciling(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/reconcile`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.recovered > 0) {
        toast.success(`Recovered ${data.recovered} missed payment${data.recovered > 1 ? "s" : ""}.`);
      } else {
        toast.success("All payments up to date — nothing missed.");
      }
      load(token);
    } catch (_) {
      toast.error("Could not check payments. Try again.");
    } finally {
      setReconciling(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this bank-transfer donation?")) return;
    try {
      await axios.delete(`${API}/admin/donations/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Deleted.");
      load(token);
    } catch (_) {
      toast.error("Could not delete.");
    }
  };

  const exportCsv = () => {
    const headers = ["Date", "Channel", "Name", "Business", "Address", "Email", "Phone", "Plaque Message", "Tax Relief", "Amount (EUR)"];
    const rows = donations.map((d) => [
      (d.donated_at || "").slice(0, 10),
      d.channel === "bank_transfer" ? "Bank Transfer" : "Card",
      d.donor_name,
      d.business_name,
      d.address,
      d.email,
      d.phone,
      d.plaque_message,
      d.tax_relief_consent ? "Yes" : "No",
      d.amount_eur,
    ]);
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ballwall-donations-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black">
        <Loader2 className="h-6 w-6 animate-spin text-white" />
      </div>
    );
  }

  if (!authed) return (<><Toaster theme="dark" position="top-center" /><LoginGate onAuthed={onAuthed} /></>);

  const total = donations.reduce((s, d) => s + (d.amount_eur || 0), 0);
  const cardCount = donations.filter((d) => d.channel !== "bank_transfer").length;
  const bankCount = donations.filter((d) => d.channel === "bank_transfer").length;

  return (
    <div className="min-h-screen bg-black px-4 py-10 text-white md:px-8">
      <Toaster theme="dark" position="top-center" />
      <div className="mx-auto w-full max-w-screen-xl">
        {/* Header */}
        <div className="mb-10 flex items-center justify-between">
          <div>
            <p className="label-caps mb-1 text-white/50">Walterstown GFC · Ball Wall</p>
            <h1 className="font-oswald text-3xl font-bold uppercase md:text-4xl">Donations Admin</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="reconcile-btn"
              onClick={reconcile}
              disabled={reconciling}
              title="Checks Stripe for any completed payments that didn't register, updates the total and sends the emails"
              className="flex items-center gap-2 border border-white/25 px-4 py-2 font-oswald text-xs uppercase tracking-[0.2em] transition-colors hover:bg-white hover:text-black disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${reconciling ? "animate-spin" : ""}`} />
              {reconciling ? "Checking…" : "Check Payments"}
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 border border-white/25 px-4 py-2 font-oswald text-xs uppercase tracking-[0.2em] transition-colors hover:bg-white hover:text-black"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>

        {/* Summary */}
        <div className="mb-8 grid grid-cols-1 gap-px border border-white/15 bg-white/15 sm:grid-cols-3">
          {[
            { label: "Total Donations", value: nf(donations.length), testid: "summary-total-count" },
            { label: "Card / Bank Split", value: `${cardCount} / ${bankCount}`, testid: "summary-split" },
            { label: "Total Value", value: `€${nf(total)}`, testid: "summary-total-value" },
          ].map((s) => (
            <div key={s.label} className="bg-black p-6">
              <p className="label-caps mb-3 text-white/55">{s.label}</p>
              <p className="font-oswald text-3xl font-bold" data-testid={s.testid}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Site Visits */}
        <VisitsPanel visits={visits} />

        <div className="mb-8">
          <AddDonation token={token} onAdded={() => load(token)} />
        </div>

        {/* Table */}
        <div className="border border-white/20 bg-[#050505]">
          <div className="flex items-center justify-between border-b border-white/15 px-6 py-5">
            <h2 className="font-oswald text-xl font-bold uppercase">All Donations</h2>
            <button
              data-testid="export-csv-btn"
              onClick={exportCsv}
              disabled={!donations.length}
              className="flex items-center gap-2 border border-white/25 px-4 py-2 font-oswald text-xs uppercase tracking-[0.2em] transition-colors hover:bg-white hover:text-black disabled:opacity-40"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-white/60" /></div>
            ) : donations.length === 0 ? (
              <p className="px-6 py-16 text-center text-white/50" data-testid="no-donations">No donations recorded yet.</p>
            ) : (
              <table className="w-full min-w-[900px] text-left text-sm" data-testid="donations-table">
                <thead>
                  <tr className="border-b border-white/15 text-white/50">
                    {["Date", "Channel", "Name", "Business", "Email", "Phone", "Plaque", "Tax", "Amount", ""].map((h) => (
                      <th key={h} className="label-caps px-4 py-3 font-normal">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {donations.map((d) => (
                    <tr key={d.id} className="border-b border-white/8 align-top hover:bg-white/5" data-testid={`donation-row-${d.id}`}>
                      <td className="whitespace-nowrap px-4 py-4 text-white/70">{(d.donated_at || "").slice(0, 10)}</td>
                      <td className="px-4 py-4">
                        <span className={`inline-block px-2 py-0.5 text-xs uppercase tracking-wider ${d.channel === "bank_transfer" ? "bg-[#D4A537] text-black" : "bg-[#43C46A] text-black"}`}>
                          {d.channel === "bank_transfer" ? "Bank" : "Card"}
                        </span>
                      </td>
                      <td className="px-4 py-4 font-medium text-white">{d.donor_name}</td>
                      <td className="px-4 py-4 text-white/70">{d.business_name || "—"}</td>
                      <td className="px-4 py-4 text-white/70">{d.email || "—"}</td>
                      <td className="whitespace-nowrap px-4 py-4 text-white/70">{d.phone || "—"}</td>
                      <td className="max-w-[220px] px-4 py-4 text-white/80">{d.plaque_message}</td>
                      <td className="px-4 py-4 text-white/70">{d.tax_relief_consent ? "Yes" : "No"}</td>
                      <td className="whitespace-nowrap px-4 py-4 font-oswald font-bold text-white">€{nf(d.amount_eur)}</td>
                      <td className="px-4 py-4">
                        {d.channel === "bank_transfer" && (
                          <button
                            data-testid={`delete-btn-${d.id}`}
                            onClick={() => remove(d.id)}
                            className="text-white/40 transition-colors hover:text-red-400"
                            aria-label="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
